"use client";

import ResponsiveContainer from "../../../layouts/ResponsiveContainer";
import EvaluationTypeIndicator from "../../../components/Cards/EvaluationTypeIndicator";
import type { IndicatorTypeEvaluation } from "../../../interfaces/Evaluations";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
    evaluationDimension,
    selectedTypeIndicator,
} from "../../../redux/slices/evaluator/evaluatorSlice";
import { useNavigate } from "react-router-dom";
import { MdAdsClick, MdInfo, MdOutlineInfo } from "react-icons/md";
import { useEffect, useState } from "react";
import Dialog from "../../../components/Dialog/Dialog";
import EvaluationDimension from "./EvaluationDimension";
import HintPopover from "../../../components/Popover/HintPopover";
import Titles from "../../../components/Titles/Titles";
import Button from "../../../components/Buttons/Button";
import useIsMobile from "../../../hooks/useIsMobile";

const EvaluationTypeOfIndicator = ({
    evaluation_indicator_type,
    right,
    rightId,
}: {
    evaluation_indicator_type: IndicatorTypeEvaluation[];
    right: string;
    rightId: string;
}) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const params = new URLSearchParams(window.location.search);

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [showHint, setShowHint] = useState(true);

    const {
        selected_type_indicator,
        evaluation_dimension,
        evaluation_subdimension,
    } = useAppSelector((state) => state.evaluator);

    useEffect(() => {
        const typeIndicator = params.get("typeIndicator") || "";
        const typeIndicatorId = params.get("typeIndicatorId") || "";

        if (typeIndicator.length > 0 && typeIndicatorId.length > 0) {
            handleClick(Number(typeIndicatorId), typeIndicator);
        }
    }, []);

    const handleClick = (typeOfIndicatorId: number, typeOfIndicatorName: string) => {
        const params = new URLSearchParams(window.location.search);
        params.set("typeIndicator", typeOfIndicatorName);
        params.set("typeIndicatorId", typeOfIndicatorId.toString());

        navigate({ search: params.toString() });
        dispatch(selectedTypeIndicator({
            typeOfIndicator: { name: typeOfIndicatorName, id: typeOfIndicatorId },
        }));
        //Para obtener la evaluaciones de las dimensiones necesita saber cual es el tipo de indicador que las contiene
        dispatch(evaluationDimension({ typeOfIndicator: typeOfIndicatorId }));
    };

    return (
        <>
            <div className="flex justify-center items-center flex-col">
                <ResponsiveContainer className="mt-20 md:mt-24 flex flex-col items-center">
                    <div className="h-full flex items-center gap-2 sm:w-max">
                        <Titles
                            title="Tipos de indicadores"
                            withBgPattern={false}
                            color='text-brand-green-20'
                        />
                        <Button
                            label=''
                            onClick={() => setIsModalVisible(!isModalVisible)}
                            icon={MdOutlineInfo}
                            iconSize={20}
                            variant='none'
                            className="!px-0 !h-full"
                        />
                    </div>
                    <div
                        className="flex flex-col md:flex-row w-full relative justify-between mt-10 gap-4"
                        id="table-evaluation-indicator-type"
                    >
                        {showHint && !selected_type_indicator?.name && !isMobile && (
                            <HintPopover
                                message="Seleccioná un tipo de indicador para visualizar la información disponible"
                                icon={<MdAdsClick size={24} className="text-brand-green-30 shrink-0" />}
                                onClose={() => setShowHint(false)}
                                left="md:left-24 xl:left-32"
                            />
                        )}
                        <div className="flex gap-1 md:gap-3 w-full">
                            {evaluation_indicator_type &&
                                evaluation_indicator_type.length > 0 &&
                                evaluation_indicator_type.map((element) => (
                                    <EvaluationTypeIndicator
                                        label={element.nombre}
                                        status={element.trivalente_tipo_indicador}
                                        size={12}
                                        key={element.id}
                                        isSelected={selected_type_indicator?.name === element.nombre}
                                        handleClick={() => handleClick(element.id, element.nombre)}
                                        numberOfIndicators={element.cantidad_indicadores_evaluados}
                                    />
                                ))}
                        </div>
                    </div>
                </ResponsiveContainer>
            </div>
            <EvaluationDimension
                evaluation_dimension={evaluation_dimension ?? []}
                selected_type_indicator={selected_type_indicator!}
                evaluation_subDimension_RES_PROC={evaluation_subdimension ?? []}
                right={right}
                rightId={rightId}
            />
            <Dialog isOpen={isModalVisible} toggle={() => setIsModalVisible(false)} bgColor='bg-brand-green-5'>
                <div className="flex justify-center mb-2">
                    <MdInfo size={32} color="#205253" />
                </div>
                <h2 className="text-center text-hsmall text-system-light-100 mb-4">
                    Información
                </h2>
                <p className="text-left font-bold text-bmedium text-brand-green-20">
                    Tipos de indicador:
                </p>
                <p className="text-left text-bmedium text-system-light-100">
                    Los distintos tipos de indicadores cumplen funciones específicas para
                    el seguimiento y evaluación de los derechos. Los indicadores
                    estructurales relevan información sobre las instituciones públicas y
                    los marcos normativos destinados a su protección. Los indicadores de
                    proceso examinan los recursos presupuestarios afectados a su
                    realización. Por último, los indicadores de resultados brindan
                    indicios - en su mayoría estadísticos - del impacto de las políticas
                    públicas sobre distintos aspectos que hacen a su efectividad. Explorá
                    los detalles en nuestra Metodología de Evaluación.
                </p>
            </Dialog>
        </>
    );
};

export default EvaluationTypeOfIndicator;